import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import { showToast } from "../utils/toastUtils";
import { format } from "date-fns";
import {
  CreditCard,
  Truck,
  Search,
  CheckCircle,
  AlertCircle,
  X,
  Check,
  DollarSign,
  Clock
} from "lucide-react";

function Payment() {
  const { token } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [modalOrderTotal, setModalOrderTotal] = useState(0);
  const [modalShippingTotal, setModalShippingTotal] = useState(0);

  const [totalPendingAmount, setTotalPendingAmount] = useState(0);
  const [totalPendingShipping, setTotalPendingShipping] = useState(0);
  const [totalPendingOrders, setTotalPendingOrders] = useState(0);
  const [lastCompletedDate, setLastCompletedDate] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Reusable Input Style
  const inputStyle = "w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 text-gray-700 placeholder-gray-400 shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none hover:border-blue-300";

  useEffect(() => {
    const fetchPackedOrders = async () => {
      try {
        if (token) {
          axios.defaults.headers.common["x-auth-token"] = token;
        }

        const res = await axios.get(`${BASE_URL}/api/orders`);
        const packedOrders = res.data.filter(
          (order) => order.status === "Packed" && order.paymentStatus === "Pending"
        );
        setOrders(packedOrders);
        setFilteredOrders(packedOrders);

        let amount = 0;
        let shipping = 0;
        packedOrders.forEach((order) => {
          amount += order.subTotal;
          shipping += order.shippingCost;
        });

        setTotalPendingAmount(amount);
        setTotalPendingShipping(shipping);
        setTotalPendingOrders(packedOrders.length);

        const completed = res.data.filter(
          (order) => order.paymentStatus === "Completed" && order.paymentCompletedAt
        );
        if (completed.length > 0) {
          const sorted = completed.sort(
            (a, b) => new Date(b.paymentCompletedAt) - new Date(a.paymentCompletedAt)
          );
          setLastCompletedDate(sorted[0].paymentCompletedAt);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch orders");
        showToast("Error", "Failed to fetch orders", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchPackedOrders();
  }, [token, BASE_URL]);

  useEffect(() => {
    let filtered = orders;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.orderId.toLowerCase().includes(q) ||
          order.trackingNumber?.toLowerCase().includes(q) ||
          order.customerName.toLowerCase().includes(q)
      );
    }
    if (filterDate) {
      filtered = filtered.filter(
        (order) => format(new Date(order.createdAt), "yyyy-MM-dd") === filterDate
      );
    }
    setFilteredOrders(filtered);
  }, [searchQuery, filterDate, orders]);

  const formatAddress = (order) => {
    return [
      order.addressLine1,
      order.addressLine2,
      order.addressLine3,
      order.city,
      order.zipCode,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const toggleSelectOrder = (orderId) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const openConfirmModal = () => {
    const selectedOrderObjects = orders.filter((order) =>
      selectedOrders.includes(order._id)
    );

    const totalOrder = selectedOrderObjects.reduce((sum, o) => sum + o.subTotal, 0);
    const totalShipping = selectedOrderObjects.reduce(
      (sum, o) => sum + (o.originalShippingCost || o.shippingCost || 0),
      0
    );

    setModalOrderTotal(totalOrder);
    setModalShippingTotal(totalShipping);
    setConfirmModalOpen(true);
  };


  const toggleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map((order) => order._id));
    }
  };

  const markAsCompleted = async () => {
    try {
      for (const orderId of selectedOrders) {
        await axios.put(`${BASE_URL}/api/orders/${orderId}/payment-status`, {
          paymentStatus: "Completed",
        });
      }

      const selectedOrderObjects = orders.filter((order) =>
        selectedOrders.includes(order._id)
      );

      const orderIds = selectedOrderObjects.map((order) => order.orderId);
      const waybillIds = selectedOrderObjects.map(
        (order) => order.trackingNumber || "-"
      );
      const totalOrderAmount = selectedOrderObjects.reduce(
        (sum, order) => sum + order.subTotal,
        0
      );
      const totalShippingAmount = selectedOrderObjects.reduce(
        (sum, order) => sum + order.originalShippingCost,
        0
      );

      await axios.post(`${BASE_URL}/api/payment-history`, {
        orderIds,
        waybillIds,
        totalOrderAmount,
        totalShippingAmount,
      });

      const updatedOrders = orders.filter(
        (order) => !selectedOrders.includes(order._id)
      );
      setOrders(updatedOrders);
      setSelectedOrders([]);
      setAcknowledged(false);
      setConfirmModalOpen(false);
      showToast("Success", "Payment marked as completed", "success");

      let amount = 0;
      let shipping = 0;
      updatedOrders.forEach((order) => {
        amount += order.subTotal;
        shipping += order.shippingCost;
      });

      setTotalPendingAmount(amount);
      setTotalPendingShipping(shipping);
      setTotalPendingOrders(updatedOrders.length);
      setLastCompletedDate(new Date().toISOString());
    } catch (err) {
      showToast("Error", "Failed to update payment status", "error");
    }
  };

  return (
    <div className="flex bg-gray-50 min-h-screen pb-12">
      <div className="flex-1 flex flex-col w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-blue-600" /> Payments & Settlements
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Track COD payments and settle shipping costs for packed orders.
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">To Receive (COD)</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                Rs. {totalPendingAmount.toLocaleString("en-LK", { minimumFractionDigits: 2 })}
              </h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
            <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600">
              <Truck className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Shipping to Pay</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                Rs. {totalPendingShipping.toLocaleString("en-LK", { minimumFractionDigits: 2 })}
              </h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
            <div className="p-3 rounded-lg bg-red-50 text-red-600">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Pending Orders</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                {totalPendingOrders}
              </h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
            <div className="p-3 rounded-lg bg-green-50 text-green-600">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Last Settlement</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">
                {lastCompletedDate ? format(new Date(lastCompletedDate), "MMM d, yyyy") : "No Data"}
              </h3>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto flex-1">
              <div className="relative group flex-1 max-w-md">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                <input
                  type="text"
                  placeholder="Search order ID, waybill, customer..."
                  className={`${inputStyle} pl-10`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="relative group max-w-xs">
                <input
                  type="date"
                  className={inputStyle}
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                />
              </div>
            </div>

            <button
              onClick={openConfirmModal}
              disabled={selectedOrders.length === 0}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium shadow-sm transition-all ${selectedOrders.length === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md"
                }`}
            >
              <CheckCircle className="w-5 h-5" />
              Mark as Completed ({selectedOrders.length})
            </button>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-500">Loading payment data...</p>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-500 flex flex-col items-center">
              <AlertCircle className="w-10 h-10 mb-2" />
              {error}
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <div className="bg-gray-50 rounded-full p-4 mb-4">
                <Search className="w-8 h-8 text-gray-300" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">No Pending Payments Found</h3>
              <p className="text-gray-500 mt-1">All packed orders have been settled or match your filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-4 py-4 text-left">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[90px]">Order ID</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[100px]">Date</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[120px]">Waybill</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[140px]">Customer</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[180px]">Address</th>
                    <th className="px-4 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[100px]">Amount</th>
                    <th className="px-4 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[100px]">Delivery</th>
                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[100px]">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order._id} className={`hover:bg-blue-50/30 transition-colors duration-150 ${selectedOrders.includes(order._id) ? "bg-blue-50/20" : ""}`}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          checked={selectedOrders.includes(order._id)}
                          onChange={() => toggleSelectOrder(order._id)}
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap align-top">
                        <div className="text-sm font-bold text-gray-900">#{order.orderId || "-"}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 align-top">
                        {order.createdAt ? format(new Date(order.createdAt), "MMM d, yyyy") : "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap align-top">
                        <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {order.trackingNumber || "N/A"}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="text-sm font-medium text-gray-900 line-clamp-2">{order.customerName}</div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <p className="text-sm text-gray-600 line-clamp-2" title={formatAddress(order)}>
                          {formatAddress(order)}
                        </p>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right align-top">
                        <span className="text-sm font-semibold text-gray-900">Rs. {order.subTotal.toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right align-top">
                        <div className="flex flex-col items-end">
                          <span className="text-sm text-gray-600">Rs. {order.originalShippingCost || order.shippingCost}</span>
                          {order.shippingCost === 0 && <span className="text-xs text-green-600 font-medium whitespace-nowrap">Free Shipping</span>}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center align-top">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${order.paymentStatus === "Completed"
                          ? "bg-green-100 text-green-800 border-green-200"
                          : "bg-yellow-100 text-yellow-800 border-yellow-200"
                          }`}>
                          {order.paymentStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Confirmation Modal */}
        {confirmModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                  Confirm Completion
                </h2>
                <button
                  onClick={() => setConfirmModalOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 mb-5 border border-blue-100">
                <p className="text-sm text-blue-800 mb-2">
                  You are marking <strong>{selectedOrders.length}</strong> orders as completed.
                </p>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-600">Total Revenue (COD):</span>
                    <span className="font-bold text-blue-900">Rs. {modalOrderTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-600">Shipping Settlement:</span>
                    <span className="font-bold text-blue-900">Rs. {modalShippingTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3 mb-6 p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer" onClick={() => setAcknowledged(!acknowledged)}>
                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${acknowledged ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}>
                  {acknowledged && <Check className="w-3 h-3 text-white" />}
                </div>
                <label className="text-sm text-gray-700 cursor-pointer select-none">
                  I acknowledge that these payments have been successfully settled.
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModalOpen(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={!acknowledged}
                  onClick={markAsCompleted}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg text-white shadow-sm transition-all ${acknowledged
                    ? "bg-blue-600 hover:bg-blue-700 hover:shadow-md"
                    : "bg-gray-300 cursor-not-allowed"
                    }`}
                >
                  Confirm & Mark
                </button>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
}

export default Payment;
